import React, { useState, useRef } from 'react';
import {
  importCategoryMappingCSV,
  ImportResult,
} from 'services/categoryImporterService';

interface CSVImporterProps {
  year: number;
  onImportComplete: () => void;
}

const CSVImporter: React.FC<CSVImporterProps> = ({
  year,
  onImportComplete,
}) => {
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImporting(true);
    setResult(null);

    try {
      const text = await file.text();
      const res = await importCategoryMappingCSV(text, year);
      setResult(res);
      if (res.success) {
        onImportComplete();
      }
    } catch (err: any) {
      setResult({
        success: false,
        message: `System error: ${err.message}`,
      });
    } finally {
      setImporting(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".csv"
          className="hidden"
        />
        <button
          onClick={triggerFileInput}
          disabled={importing}
          className={`text-xs font-semibold font-baijamjuree px-3 py-1.5 rounded border transition-colors ${
            importing
              ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
              : 'border-blue-200 bg-blue-50 text-blue-600 hover:bg-blue-100'
          }`}
        >
          {importing ? '⌛ Importing...' : '↑ Import CSV'}
        </button>
      </div>

      {result && (
        <div
          className={`text-[10px] p-2 rounded border mt-1 max-w-xs overflow-auto max-h-32 ${
            result.success
              ? 'bg-green-50 border-green-100 text-green-700'
              : 'bg-red-50 border-red-100 text-red-700'
          }`}
        >
          <div className="font-bold mb-1">{result.message}</div>
          {result.details && result.details.length > 0 && (
            <ul className="list-disc list-inside opacity-80">
              {result.details.map((detail, i) => (
                <li key={i}>{detail}</li>
              ))}
            </ul>
          )}
          <button
            onClick={() => setResult(null)}
            className="mt-2 underline hover:no-underline"
          >
            Clear
          </button>
        </div>
      )}
    </div>
  );
};

export default CSVImporter;
